import { Chip } from '../../components/ui/Card';
import { Explicit } from '../../components/ui/Explicit';
import { WaveformScan } from '../../components/ui/Loader';
import { ProvenanceChip, type Provenance } from '../tracks/provenance';

/**
 * The track, as it will look, while the form is still being filled in.
 *
 * ## Why a preview at all
 *
 * The form asks nine questions and gives nothing back until the very end,
 * when the track page appears. Every answer is typed into a box and vanishes
 * into it. This draws the page they are heading for as they go: a cover
 * lands in the square when it is picked, a title replaces "Untitled" as it
 * is typed, a chip appears when a choice is made. It is the difference
 * between filling in a form and making a thing.
 *
 * ## The same shapes as the real page
 *
 * Cover, title, handle, chips, a play row. The proportions are the track
 * page hero's and the chips are the same components the track list draws,
 * so what it shows is what they get - a preview that flatters is a preview
 * that disappoints five minutes later.
 *
 * ## It carries the wait, too
 *
 * Once the button is pressed the form freezes and this is the thing worth
 * watching: a ring around the cover fills as the file goes up, then the
 * waveform scans while the streaming versions are built, then a check. The
 * same object that showed what the track would be shows it becoming that.
 */
export interface PreviewState {
  cover: string | null;
  title: string;
  artist: string;
  genre: string | null;
  visibility: 'public' | 'unlisted' | 'private';
  explicit: boolean;
  provenance: Provenance | '';
  description: string;
  hasFile: boolean;
  fileLabel: string | null;
  phase: 'choosing' | 'uploading' | 'processing' | 'timeout' | 'failed' | 'done';
  percent: number;
}

const BARS = [0.35, 0.7, 1, 0.55, 0.85, 0.4, 0.9, 0.6, 0.3, 0.75, 0.5, 0.95];

/* The ring: a circle of this radius, so the dash length is its circumference. */
const RING_R = 46;
const RING_C = 2 * Math.PI * RING_R;

export function UploadPreview({
  state,
  compact = false,
}: {
  state: PreviewState;
  compact?: boolean;
}) {
  const busy = state.phase === 'uploading' || state.phase === 'processing';

  if (compact) {
    return (
      <div className="flex items-center gap-3" data-upload-preview="compact">
        <Cover state={state} size="h-16 w-16" />
        <div className="min-w-0 flex-1">
          <Title title={state.title} className="truncate text-[15px] font-semibold" />
          <p className="meta truncate text-accent">@{state.artist}</p>
          <Chips state={state} className="mt-1.5" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-[var(--radius-panel)] border border-line bg-raised/40 p-5"
      data-upload-preview="full"
    >
      <p className="meta-caps text-faint">
        {busy ? 'On its way' : state.phase === 'done' ? 'Ready' : 'How it will look'}
      </p>

      <div className="mt-4">
        <Cover state={state} size="aspect-square w-full" />
      </div>

      <Title title={state.title} className="mt-4 text-xl font-bold leading-tight tracking-tight" />
      <p className="meta mt-1 text-accent">@{state.artist}</p>

      <Chips state={state} className="mt-3" />

      {/* The play row: the same shape as a track row's, with bars that idle
          once there is a file to imagine playing. */}
      <div className="mt-4 flex items-center gap-3">
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-pill)] transition-colors ${
            state.hasFile ? 'bg-accent text-accent-ink' : 'bg-line text-faint'
          }`}
          aria-hidden
        >
          <svg viewBox="0 0 16 16" className="h-4 w-4 translate-x-px" fill="currentColor">
            <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
          </svg>
        </span>
        <Bars live={state.hasFile} />
        <span className="meta-caps tabular-nums text-faint">{state.fileLabel ?? '--:--'}</span>
      </div>

      {state.description.trim() !== '' && (
        <p
          key="description"
          className="mt-4 line-clamp-3 whitespace-pre-wrap text-[13px] leading-relaxed text-soft"
          style={{ animation: 'gv-fade-in 240ms ease-out' }}
        >
          {state.description}
        </p>
      )}
    </div>
  );
}

function Title({ title, className }: { title: string; className: string }) {
  const trimmed = title.trim();

  return (
    <p
      key={trimmed === '' ? 'empty' : 'set'}
      className={`${className} ${trimmed === '' ? 'text-faint' : 'text-ink'}`}
      style={{ animation: 'gv-step-in 240ms cubic-bezier(0.22,1,0.36,1)' }}
      data-preview-title
    >
      {trimmed === '' ? 'Untitled' : trimmed}
    </p>
  );
}

function Chips({ state, className }: { state: PreviewState; className: string }) {
  const pop = { animation: 'gv-pop 260ms cubic-bezier(0.22,1,0.36,1)' };

  return (
    <div className={`flex min-h-[15px] flex-wrap items-center gap-1.5 ${className}`}>
      {state.genre !== null && (
        <span key={`genre-${state.genre}`} style={pop} className="inline-flex">
          <Chip>{state.genre}</Chip>
        </span>
      )}
      {state.visibility !== 'public' && (
        <span key={`vis-${state.visibility}`} style={pop} className="inline-flex">
          <Chip>{state.visibility}</Chip>
        </span>
      )}
      {state.explicit && (
        <span key="explicit" style={pop} className="inline-flex">
          <Explicit />
        </span>
      )}
      {state.provenance !== '' && (
        <span key={`prov-${state.provenance}`} style={pop} className="inline-flex">
          <ProvenanceChip value={state.provenance} />
        </span>
      )}
    </div>
  );
}

/**
 * The cover square, and everything that happens on top of it.
 *
 * The ring is an SVG circle whose dash offset follows the upload percentage;
 * a transition on the offset means it moves smoothly between the progress
 * events rather than stepping. Processing swaps the ring for the scanning
 * waveform, because that wait is not measurable and a ring that never fills
 * would be a lie.
 */
function Cover({ state, size }: { state: PreviewState; size: string }) {
  const offset = RING_C * (1 - Math.min(100, Math.max(0, state.percent)) / 100);
  const dimmed =
    state.phase === 'uploading' || state.phase === 'processing' || state.phase === 'done';

  return (
    <div
      className={`relative shrink-0 overflow-hidden rounded-[var(--radius-card)] border border-line ${size}`}
      data-preview-cover={state.cover !== null ? 'set' : 'empty'}
    >
      {state.cover !== null ? (
        <img
          key={state.cover}
          src={state.cover}
          alt=""
          className={`h-full w-full object-cover transition-[filter,opacity] duration-300 ${
            dimmed ? 'opacity-40 blur-[1px]' : ''
          }`}
          style={{ animation: 'gv-fade-in 320ms ease-out' }}
        />
      ) : (
        <div
          aria-hidden
          className="flex h-full w-full items-center justify-center"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, var(--color-blank) 0 6px, var(--color-blank-alt) 6px 12px)',
          }}
        >
          {!dimmed && <span className="meta-caps text-faint">Cover</span>}
        </div>
      )}

      {state.phase === 'uploading' && (
        <div className="absolute inset-0 flex items-center justify-center" data-preview-ring>
          <svg viewBox="0 0 120 120" className="h-[62%] w-[62%] -rotate-90">
            <circle
              cx="60"
              cy="60"
              r={RING_R}
              fill="none"
              stroke="var(--color-line-mid)"
              strokeWidth="6"
            />
            <circle
              cx="60"
              cy="60"
              r={RING_R}
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={RING_C}
              strokeDashoffset={offset}
              style={{ transition: 'stroke-dashoffset 220ms ease-out' }}
            />
          </svg>
          <span className="absolute font-mono text-sm tabular-nums text-ink">{state.percent}%</span>
        </div>
      )}

      {state.phase === 'processing' && (
        <div className="absolute inset-0 flex items-center justify-center" data-preview-scan>
          <WaveformScan className="w-[60%]" />
        </div>
      )}

      {state.phase === 'done' && (
        <div className="absolute inset-0 flex items-center justify-center" data-preview-done>
          <span
            className="flex h-14 w-14 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-ink"
            style={{ animation: 'gv-pop 360ms cubic-bezier(0.22,1,0.36,1)' }}
          >
            <svg
              viewBox="0 0 20 20"
              className="h-7 w-7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
            >
              <path d="m4.5 10.5 3.6 3.6L15.5 6.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      )}
    </div>
  );
}

/** Twelve bars that breathe once there is something to play, and hold still until then. */
function Bars({ live }: { live: boolean }) {
  return (
    <span className="flex h-6 flex-1 items-end gap-[3px]" aria-hidden>
      {BARS.map((height, i) => (
        <span
          key={i}
          className={`w-full origin-bottom rounded-[1px] ${live ? 'bg-accent' : 'bg-line-strong'}`}
          style={{
            height: `${height * 100}%`,
            animation: live ? `gv-bar-soft 1.05s ease-in-out ${i * 0.11}s infinite` : undefined,
          }}
        />
      ))}
    </span>
  );
}
